/**
 * src/utils/auth.js
 * 認證相關的工具函數（httpOnly cookie + CSRF）
 */
import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

// 全域 axios 設定：所有請求都帶 cookie
axios.defaults.withCredentials = true;

// 從 cookie 讀 CSRF token
export const getCsrfToken = () => {
    const match = document.cookie.match(/(?:^|;\s*)sgs_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};

// 防止多個 401 同時觸發多次 refresh
let refreshPromise = null;

/**
 * 安裝 axios 攔截器：自動帶 CSRF header，401 時呼叫 refresh
 * onLogout: refresh 失敗時呼叫的清除 state callback
 */
export const setupAxiosInterceptors = (onLogout) => {
    // 移除舊的 interceptor，避免重複安裝
    axios.interceptors.request.handlers = [];
    axios.interceptors.response.handlers = [];

    // Request: 自動帶 CSRF header（unsafe methods）
    axios.interceptors.request.use((config) => {
        config.withCredentials = true;
        const method = (config.method || 'get').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
            const csrf = getCsrfToken();
            if (csrf && !config.headers['X-CSRF-Token']) {
                config.headers['X-CSRF-Token'] = csrf;
            }
        }
        return config;
    });

    // Response: 401 時嘗試 refresh
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            if (
                error.response?.status === 401 &&
                !originalRequest._retry &&
                !originalRequest.url?.includes('/token')
            ) {
                originalRequest._retry = true;
                try {
                    if (!refreshPromise) {
                        refreshPromise = axios.post(
                            `${backendUrl}/token/refresh`,
                            null,
                            { withCredentials: true }
                        ).finally(() => { refreshPromise = null; });
                    }
                    await refreshPromise;
                    // cookie 已由後端更新，重試原請求
                    return axios(originalRequest);
                } catch (refreshErr) {
                    onLogout?.();
                    return Promise.reject(refreshErr);
                }
            }
            return Promise.reject(error);
        }
    );
};

// 載入外部資源（如 Bootstrap CSS/JS 和圖示）
export const loadExternalResources = (resources) => {
    resources.forEach(({ type, url }) => {
        if (type === 'css' && !document.querySelector(`link[href="${url}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;
            document.head.appendChild(link);
        }
        if (type === 'js' && !document.querySelector(`script[src="${url}"]`)) {
            const script = document.createElement('script');
            script.src = url;
            document.head.appendChild(script);
        }
    });
};

// 手動 JWT 解碼函數
export const manualJwtDecode = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("Failed to decode token manually:", e);
        return null;
    }
};
